import React, { useEffect, useMemo, useState } from "react";
import * as S from "./styles";
import { activities, visits, all, todo, favorites } from "../../utils";
import FilterFullText from "./FilterFullText/FilterFullText";
import Header from "./Header/Header";
import List from "./List/List";
import { useMain, useMainState } from "modules/app/contexts";
import { EmptySection } from "./EmptySection";
import * as http from "modules/app/utils/http";
import * as _ from "lodash";
import { useTranslation } from "react-i18next";
import MultipleForward from "./MultipleForward/MultipleForward";
import { Filters } from "./Filters";

const filters = [todo, visits, activities, favorites, all];
const filterNames = ["todo", "visits", "activities", "favorites", "all"];

const countTotal = (tab) =>
  tab.reduce((total, item) => (total += item.items.length), 0);

export default function ContactList({ contacts }) {
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const [forwardModal, setForwardModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const { t } = useTranslation(["Search"]);

  const { updateContacts, overwriteTotals, contactsLoading } = useMain();
  const { isContactsLoading } = useMainState();

  const {
    totalActivities,
    totalAll,
    totalFavorites,
    totalTodo,
    totalVisits,
    setSelectedForwardContacts,
    selectedForwardContacts,
    filterUsers,
    filterTags,
    filterScope,
    filterTeams,
    user,
    searchContacts,
    forwardMode,
  } = useMainState();

  const isFilterActive =
    !_.isEmpty(filterUsers) ||
    !_.isEmpty(filterTags) ||
    !_.isEmpty(filterScope) ||
    !_.isEmpty(filterTeams);

  const tabTodo = useMemo(() => (totalTodo ? totalTodo : "..."), [totalTodo]);

  const tabVisits = useMemo(() => (totalVisits ? totalVisits : "..."), [
    totalVisits,
  ]);

  const tabActivities = useMemo(
    () => (totalActivities ? totalActivities : "..."),
    [totalActivities]
  );

  const tabFavorites = useMemo(
    () => (totalFavorites ? totalFavorites : "..."),
    [totalFavorites]
  );

  const tabAll = useMemo(() => (totalAll ? totalAll : "..."), [totalAll]);

  const handleChange = (_event, newValue) => {
    setTabValue(newValue);
  };

  async function loadMoreContacts() {
    if (contacts.length <= totalAll && page < totalPages) {
      try {
        const { data } = await http.listCustomers(
          page + 1,
          filterUsers,
          filterNames[tabValue],
          filterTags,
          filterScope?.name
        );
        setPage((prevState) => prevState + 1);
        updateContacts(_.uniqBy([...contacts, ...data], "id"));
      } catch (error) {
        console.log(error);
      }
    }
  }

  const contactList = useMemo(() => {
    return contacts;
  }, [contacts]);

  function totalContactsFromTab(tabValue) {
    switch (tabValue) {
      case 0: // tab todo
        return tabTodo;
      case 2: // tab activities
        return tabActivities;
      case 3: // tab favorites
        return tabFavorites;
      case 4: // tab all
        return tabAll;
      default:
        return 0;
    }
  }

  function handleUnselectAll() {
    setSelectedForwardContacts([]);
  }

  async function handleSubmitForward() {
    setForwardModal(true);
  }

  async function setTotalPageByTabType() {
    const total = await http.getTabTotalPages(
      filterUsers,
      filterNames[tabValue],
      filterTags
    );
    setTotalPages(total);
  }

  async function loadCustomersByFilter() {
    setPage(1);

    contactsLoading(true);

    const [data, filteredTotals] = await http
      .getAllTabsCustomers(filterUsers, filterTags, filterScope?.name)
      .finally(() => contactsLoading(false));

    setTotalPageByTabType();
    updateContacts([...data]);
    overwriteTotals(filteredTotals);
  }

  function handleLastContactVisible(lastContact) {
    if (lastContact) {
      loadMoreContacts();
    }
  }

  useEffect(() => {
    setPage(1);

    setTotalPageByTabType();
  }, [tabValue]);

  useEffect(() => {
    if (isFilterActive) {
      loadCustomersByFilter();
    }
  }, [filterUsers, filterTags, filterScope, filterTeams]);

  return (
    <S.Container>
      <S.SearchContainer>
        <FilterFullText searchText={searchText} setSearchText={setSearchText} />

        {!searchText && !forwardMode && (
          <Filters isFilterActive={isFilterActive} />
        )}
      </S.SearchContainer>

      {searchText && !forwardMode && user?.role == "manager" && (
        <S.FilterActive>
          <S.FilterActiveText>{t("Search:search_teams")}</S.FilterActiveText>
        </S.FilterActive>
      )}

      {!searchText && !forwardMode && (
        <Header
          tabTodo={tabTodo}
          tabVisits={tabVisits}
          tabActivities={tabActivities}
          tabFavorites={tabFavorites}
          tabAll={tabAll}
          contacts={contactList}
          handleChange={handleChange}
          tabValue={tabValue}
        />
      )}

      {forwardMode && (
        <S.ForwardModeContainer>
          <S.ForwardSelectedCounter>
            {t("Contacts:forwardMode.selectedContacts", {
              count: selectedForwardContacts.length,
            })}
          </S.ForwardSelectedCounter>
          <S.ForwardUnselectButton onClick={handleUnselectAll}>
            <S.UnselectIcon />
            {t("Contacts:forwardMode.unselectAll")}
          </S.ForwardUnselectButton>

          <S.ForwardButton onClick={handleSubmitForward}>
            <S.ForwardIcon />
            {t("Contacts:forwardMode.forwardAll")}
          </S.ForwardButton>
        </S.ForwardModeContainer>
      )}

      {!searchText || searchText === "" ? (
        !isContactsLoading ? (
          <S.Content>
            {filters[tabValue](contactList).map((contactList) => (
              <List
                handleLastContactVisible={handleLastContactVisible}
                key={contactList.title}
                section={{
                  items: contactList.items,
                  title: contactList.title,
                }}
                total={totalContactsFromTab(tabValue)}
                EmptySection={() => (
                  <EmptySection type={filterNames[tabValue]} />
                )}
              />
            ))}
          </S.Content>
        ) : (
          <S.LoadingContainer>
            <S.Loading />
          </S.LoadingContainer>
        )
      ) : (
        <S.Content>
          {searchContacts?.length > 0 || searchText ? (
            <List
              handleLastContactVisible={handleLastContactVisible}
              key={"Pesquisa"}
              total={searchContacts?.length || 0}
              section={{
                items: searchContacts,
                title: t("Search:result"),
              }}
              EmptySection={() => <EmptySection type="search" />}
            />
          ) : (
            <S.Title>{t("Search:searching")}</S.Title>
          )}
        </S.Content>
      )}
      {forwardModal && (
        <MultipleForward handleClose={() => setForwardModal(false)} />
      )}
    </S.Container>
  );
}
